import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import { ApiCallError } from '@src/lib/api_client'
import Member from '@src/data/member'
import { Button } from '@src/elements/button/button'
import { ActionBar } from '@src/components/action-bar/action-bar'
import { computeLastActivityFromAlerts, objectToQueryParamString, track } from '@src/util/functions'
import { SearchMember } from '@src/routes/members/components/list/search-member'
import { MemberStateFilter } from '@src/routes/members/components/list/member-state-filter'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { Link } from '@src/elements/link/link'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { MemberService } from '@src/service/member'

import './members-list.sass'
import { TableList } from '@src/components/table-list/table-list'
import { ListColumnHeaders } from '@src/components/table-list/header'
import { NameCell } from '@src/routes/members/components/list/name-cell'
import { ActivityCell } from '@src/routes/members/components/list/activity-cell'
import { ProgressCell } from '@src/routes/members/components/list/progress-cell'
import { FlagsCell } from '@src/routes/members/components/list/flags-cell'
import { ScheduledDeactivationDateCell } from '@src/routes/members/components/list/scheduled-deactivation-date-cell'
import { Filter } from '@src/routes/content/components/filter'
import { DEFAULT_PAGE_SIZE, retentionFlags, UI_RETENTION_FLAG_TO_BACKEND_FLAG } from '@src/util/constants'
import { convertToLocalDate } from '@src/util/date-format'
import { MemberFilters } from '@src/state/store'
import XLSX from 'xlsx'
import { Workbook } from '@src/util/download-workbook'

@Component({
  computed: {
    filters () {
      return this.$store.state.memberFilters
    }
  }
})
export class PaginatedMembersList extends Vue {

  private loading: boolean = true
  private error: ApiCallError | null = null

  private paginatedMembers: Member[] = []
  private exportMembersData: Member[] = []
  private filters: MemberFilters
  private totalPages: number
  private counters: any

  constructor () {
    super()
    this.$store.dispatch('setSelectedMember', new Member())
    track('/members')
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')}`
  }

  resetFilters () {
    return this.$store.dispatch('filterMembers', { name: '', state: undefined, toBeDeactivated: false, flags: new Set([]), page: 1 })
  }

  filterByFlags (value) {
    const mappedValue = UI_RETENTION_FLAG_TO_BACKEND_FLAG[value]
    let selectedFlags = this.filters.flags
    if (selectedFlags.has(mappedValue)) {
      selectedFlags.delete(mappedValue)
    } else {
      selectedFlags.add(mappedValue)
    }
    this.$store.dispatch('filterMembers', { flags: selectedFlags })
  }

  clearFlags (flags: string[]) {
    this.$store.dispatch('filterMembers', { flags: new Set([]) })
  }

  composeAlertsToExport (member: Member): string {
    const alertsFiltered = this.filters.flags.size > 0 ? this.filters.flags : null
    let alerts: any[] = []
    for (let flag in member.progression) {
      switch (flag) {
        case 'easyWorkoutAlert':
          if (member.progression.easyWorkoutAlert && member.progression.averageIntensityRating && (alertsFiltered ? alertsFiltered.has(flag) : true)) {
            alerts.push(this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.EASYWORKOUTALERT', { valueForAlert: member.progression.averageIntensityRating }))
          }
          break
        case 'attendanceAlert14':
        case 'attendanceAlert30':
        case 'attendanceAlert60':
          if (member.progression[flag] && member.progression.lastWorkoutDate && (alertsFiltered ? alertsFiltered.has(flag) : true)) {
            const date = convertToLocalDate(member.progression.lastWorkoutDate)
            alerts.push(this.$t(`PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.${flag.toUpperCase()}`, { lastTrainingDate: date }))
          }
          break
        case 'staleWorkoutAlert':
        case 'currentWorkoutAlert':
        case 'plannedCheckupAlert':
        case 'staleCheckupAlert':
          if (member.progression[flag] && (alertsFiltered ? alertsFiltered.has(flag) : true)) {
            alerts.push(this.$t(`PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.${flag.toUpperCase()}`))
          }
          break
        case 'exercisesToChangeUp':
        case 'exercisesToChangeDown':
          if (member.progression[flag] && (alertsFiltered ? alertsFiltered.has('exercisesToChange') : true)) {
            alerts.push(this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.EXERCISESTOCHANGE'))
          }
          break
        default:
        // noop
      }
    }

    if (member.termsReacceptanceRequired && (alertsFiltered ? alertsFiltered.has('termsReacceptanceRequired') : true)) {
      alerts.push(this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS.TERMSREACCEPTANCEREQUIRED'))
    }

    return alerts.join('\r')
  }

  formatUsersToExport (): any[] {
    return this.exportMembersData.map(member => {
      let memberRow = {}
      memberRow[`${this.$t('FIRST_NAME')}`] = member.firstName || ''
      memberRow[`${this.$t('LAST_NAME')}`] = member.lastName || ''
      if (this.filters.toBeDeactivated) {
        memberRow[`${this.$t('PAGE.MEMBERS.LIST.HEADERS.SCHEDULED_DEACTIVATION_DATE')}`] = member.scheduledDeactivationDate ? convertToLocalDate(member.scheduledDeactivationDate) : ''
      }
      memberRow[`${this.$t('USERNAME')}`] = member.username || ''
      memberRow[`${this.$t('LAST_TRAINING')}`] = member.lastActivityDate ? convertToLocalDate(member.lastActivityDate) : this.$t(computeLastActivityFromAlerts(member))
      memberRow[`${this.$t('EMAIL')}`] = member.email || ''
      memberRow[`${this.$t('MOBILE_PIX_APP')}`] = member.mobile ? this.$t('YES') : this.$t('NO')
      memberRow[`${this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS_FILTER')}`] = this.composeAlertsToExport(member)

      return memberRow
    })
  }

  async setDetailsOfMembers (items: any[], destination: any[] = this.paginatedMembers) {
    const membersToLoad = items.filter(item => !item.detailsLoaded)
    if (this.loading || !membersToLoad.length) return
    this.loading = true
    await new MemberService().getMembersDetail(membersToLoad.map((m) => m.id))
      .then((memberDetails) => this.addDetailsToMembers(destination, memberDetails))
      .finally(() => {
        // the list asks for details on every render, members without details must not be requested again
        membersToLoad.forEach((member) => member.detailsLoaded = true)
        this.loading = false
      })
  }

  async setupExport () {
    const progression = Array.from(this.filters.flags)
    const params: any = {
      name_or_code: this.filters.name,
      state: this.filters.state,
      terms_reacceptance_required: Array.from(this.filters.flags).filter((flag) => flag === 'with_terms_reacceptance_required').length > 0,
      progression
    }
    if (this.filters.toBeDeactivated) {
      params.to_be_deactivated = true
    }

    try {
      const response = await new MemberService().getMembers(objectToQueryParamString(params))
      this.exportMembersData = response.members
      await this.setDetailsOfMembers(this.exportMembersData, this.exportMembersData)
      const formattedUsers = this.formatUsersToExport()
      this.export(formattedUsers)
    } catch (e) {
      this.$store.dispatch('showToast', { message: this.$t('ERRORS.API.UNKNOWN'), fadeOut: false })
    }
  }

  export (usersToExport) {
    let workSheet = XLSX.utils.json_to_sheet(usersToExport)
    // width for each column in Excel's "Max Digit Width"
    workSheet['!cols'] = this.filters.toBeDeactivated
      ? [{ width: 20 }, { width: 20 }, { width: 30 }, { width: 30 }, { width: 20 }, { width: 30 }, { width: 15 }, { width: 30 }, { width: 150 }]
      : [{ width: 20 }, { width: 20 }, { width: 30 }, { width: 20 }, { width: 30 }, { width: 15 }, { width: 30 }, { width: 150 }]
    let workBook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workBook, workSheet, `${this.$t('USERS')}`)
    Workbook.download(workBook, `${this.$t('USERS')}.xlsx`)
  }

  addDetailsToMembers (members: Member[], membersDetails): Member[] {
    // compose the member data with the details received, recent activity and training plan progress (by member id, for paged members)
    membersDetails.forEach(details => {
      const member = members.find((member) => details.id === member.id)
      if (member) {
        member.recentActivity = details.recentActivity
        member.trainingPlanProgress = details.trainingPlanProgress
        member.detailsLoaded = true
      }
    })
    this.loading = false
    return members
  }

  render (h: CreateElement) {
    const usersToExport = this.formatUsersToExport()
    const memberCells = this.filters.toBeDeactivated
      ? {
        fullName: NameCell,
        scheduledDeactivationDate: ScheduledDeactivationDateCell,
        recentActivity: ActivityCell,
        trainingProgress: ProgressCell,
        retentionFlags: FlagsCell
      }
      : {
        fullName: NameCell,
        recentActivity: ActivityCell,
        trainingProgress: ProgressCell,
        retentionFlags: FlagsCell
      }
    const memberHeader = this.filters.toBeDeactivated
      ? {
        fullName: {
          headerLabel: {
            firstName: { headerLabel: this.$t('FIRST_NAME'), type: 'string', disableSorting: true },
            lastName: { headerLabel: this.$t('LAST_NAME'), type: 'string', disableSorting: true }
          },
          type: 'object'
        },
        scheduledDeactivationDate: {
          headerLabel: <span class='scheduled-deactivation-date-header'>{this.$t('PAGE.MEMBERS.LIST.HEADERS.SCHEDULED_DEACTIVATION_DATE')}</span>,
          type: 'date',
          disableSorting: true
        },
        recentActivity: {
          headerLabel: this.$t('PAGE.MEMBERS.LIST.HEADERS.LAST_TRAINING'),
          type: 'date',
          disableSorting: true
        },
        trainingProgress: {
          headerLabel: this.$t('PAGE.MEMBERS.LIST.HEADERS.PROGRESS'),
          type: 'float',
          disableSorting: true
        },
        retentionFlags: this.renderRetentionFlagsHeader(h, usersToExport)
      }
      : {
        fullName: {
          headerLabel: {
            firstName: { headerLabel: this.$t('FIRST_NAME'), type: 'string', disableSorting: true },
            lastName: { headerLabel: this.$t('LAST_NAME'), type: 'string', disableSorting: true }
          },
          type: 'object'
        },
        recentActivity: {
          headerLabel: this.$t('PAGE.MEMBERS.LIST.HEADERS.LAST_TRAINING'),
          type: 'date',
          disableSorting: true
        },
        trainingProgress: {
          headerLabel: this.$t('PAGE.MEMBERS.LIST.HEADERS.PROGRESS'),
          type: 'float',
          disableSorting: true
        },
        retentionFlags: this.renderRetentionFlagsHeader(h, usersToExport)
      }
    return <div class='members-list'>
      <ActionBar>
        <SearchMember />

        <MemberStateFilter counters={this.counters} />

        <Button class='new-member-button' ref='newMemberButton' type='elevated' click={() => this.$router.push('/trainers/platform/members/create')}
                label={this.$t('PAGE.MEMBERS.LIST.CREATE_MEMBER')} />
      </ActionBar>

      {this.error ?
          <ErrorScreen title={this.$t('PAGE.MEMBERS.REQUEST_ERROR')} message={this.$t(this.error.messageKey)} icon='brand/face-angry'>
            <Link align='center' click={this.loadMembersByFilters} label={this.$t('TRY_AGAIN')} variant='colored' />
          </ErrorScreen>

          : (
            (this.counters && this.getEffectiveTotal() === 0
              || (!this.counters && this.paginatedMembers.length === 0 && this.totalPages === 0)
              || (this.paginatedMembers.length === 0 && !this.loading && !this.hasFilters())) ?
              <ErrorScreen title={this.$t('GET_STARTED')} message={this.$t('PAGE.MEMBERS.LIST.NO_MEMBERS')}
                           icon='brand/add-user' />
            :
            <div class={`member-list-wrapper member-scroll ${this.paginatedMembers.length ? '' : 'member-list-wrapper-empty'}`} id='member-list-wrapper'>
              <TableList
                loading={this.loading}
                ref='memberList'
                items={this.paginatedMembers}
                itemsArePaginated
                totalItems={this.totalPages * DEFAULT_PAGE_SIZE}
                cells={memberCells}
                header={memberHeader}
                changePageCallback={async (page: number) => {
                  this.$store.dispatch('filterMembers', { page })
                  document.getElementById('member-list-wrapper')!.scrollTop = 0
                }}
                currentPage={this.filters.page}
                callbackOnNewItemsOnPage={this.setDetailsOfMembers}
              />
              {this.loading ? <Loader /> : ''}
            </div>
          )
      }
    </div>
  }

  created () {
    return this.loadMembersByFilters()
  }

  beforeDestroy () {
    this.$store.dispatch('closeToast')
  }

  @Watch('filters', { deep: true })
  async showMembers () {
    return this.loadMembersByFilters()
  }

  private hasFilters () {
    if (this.filters.name || this.filters.flags.size > 0 || this.filters.state || this.filters.toBeDeactivated) return true
    return false
  }

  private getEffectiveTotal (): number {
    if (this.filters.toBeDeactivated) {
      if (!this.counters) return this.paginatedMembers.length
      const val = this.counters.toBeDeactivated || this.counters.withToBeDeactivated
      return val !== null && val !== undefined ? val : this.paginatedMembers.length
    }
    if (this.filters.state && this.counters) {
      const val = this.counters[this.filters.state]
      return val !== null && val !== undefined ? val : 0
    }
    return this.counters ? this.counters.total : 0
  }

  private loadMembersByFilters () {
    this.loading = true
    const progression = Array.from(this.filters.flags)
    const params: any = {
      page: this.filters.page,
      items_per_page: DEFAULT_PAGE_SIZE,
      name_or_code: this.filters.name,
      state: this.filters.state,
      terms_reacceptance_required: Array.from(this.filters.flags).filter((flag) => flag === 'with_terms_reacceptance_required').length > 0,
      progression
    }
    if (this.filters.toBeDeactivated) {
      params.to_be_deactivated = true
    }

    const encodedParams = objectToQueryParamString(params)
    return new MemberService().getMembers(encodedParams)
      .then((response) => {
        this.paginatedMembers = response.members
        this.totalPages = response.meta.totalPages
        this.counters = response.meta.counters
      })
      .catch((error) => this.error = error)
      .finally(() => this.loading = false)
  }

  private renderRetentionFlagsHeader (h: CreateElement, usersToExport): ListColumnHeaders[string] {
    return {
      headerLabel: <div class='flags-header-cell'>
        <Filter cssClass='retention-flags'
                mapToBackendFilters
                tag={'retention_flags'}
                counter={retentionFlags.map((flag) => new MemberService().getRetentionFlagCounter(flag, this.counters))}
                filterList={retentionFlags}
                mappedFilters={retentionFlags.map((flag) => UI_RETENTION_FLAG_TO_BACKEND_FLAG[flag])}
                placeholder={this.$t('PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.ALERTS_FILTER')}
                translation={'PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.FILTERS.'}
                selectedFilters={this.filters.flags}
                filterCallback={this.filterByFlags}
                clearAllCallback={this.clearFlags} />
        {!/iPad|iPhone|iPod/.test(navigator.userAgent || navigator.vendor) ?
          <Button type='remove' label={this.$t('EXPORT')} click={this.setupExport}
                  class='export-btn' ref='exportBtn' />
          : <vue-json-to-csv json-data={usersToExport}>
            <Button type='remove' label={this.$t('EXPORT')} class='export-btn' ref='exportBtn' />
          </vue-json-to-csv>}
      </div>,
      type: 'html'
    }
  }
}
